/**
 * Types and functions for working with 2d axis-aligned bounding boxes (AABB2)
 * @module
 */
import type { AABB2 } from "./aabb2Types";
import { AABB2GridPosition, aabb2New } from "./aabb2Types";
import { DIST_EPSILON, DIST_EPSILON_SQUARED } from "./core";
import { mat3Dot, mat3RotateAroundPoint } from "./mat3";
import type { Plane } from "./plane";
import { planeEval, planeProject } from "./plane";
import type { PolyLine2 } from "./polyLine2Types";
import type { Vec2 } from "./vec2";
import { vec2Dist, vec2Result } from "./vec2";

// Re-export for backwards compatibility
export { aabb2Height, aabb2New, aabb2Width } from "./aabb2Types";

const { min, max, sqrt } = Math;

export function intervalsOverlap(
  a1: number,
  a2: number,
  b1: number,
  b2: number,
  epsilon = DIST_EPSILON,
  inclusive: boolean = false,
): boolean {
  if (inclusive) {
    epsilon *= -1;
  }

  const x1 = min(a1, a2) - epsilon;
  const x2 = max(a1, a2) + epsilon;
  const y1 = min(b1, b2) - epsilon;
  const y2 = max(b1, b2) + epsilon;

  return (x1 >= y1 && x1 <= y2) || (x2 >= y1 && x2 <= y2) || (y1 >= x1 && y1 <= x2) || (y2 >= x1 && y2 <= x2);
}

/**
 * Initializes a degenerate AABB2 that is useful for expansion with aabb2Union or aabb2Expand
 *
 * @returns new AABB2
 */
export function aabb2Init(): AABB2 {
  return {
    minX: Infinity,
    minY: Infinity,
    maxX: -Infinity,
    maxY: -Infinity,
  };
}

export function aabb2ByTwoPoints(ax: number, ay: number, bx: number, by: number): AABB2 {
  if (ax > bx) {
    const t = ax;
    ax = bx;
    bx = t;
  }

  if (ay > by) {
    const t = ay;
    ay = by;
    by = t;
  }

  return aabb2New(ax, ay, bx, by);
}

export function aabb2ByPoints(points: Vec2[]): AABB2 {
  let firstPointIndex = -1;
  for (let i = 0; i < points.length; i += 1) {
    const point = points[i];
    if (!point || point.length < 2) {
      continue;
    }
    if (!Number.isFinite(point[0]) || !Number.isFinite(point[1])) {
      continue;
    }
    firstPointIndex = i;
    break;
  }

  if (firstPointIndex < 0) {
    return aabb2Init();
  }

  const [x1, y1] = points[firstPointIndex];
  const bb = aabb2New(x1, y1, x1, y1);
  for (let i = firstPointIndex + 1, l = points.length; i < l; i += 1) {
    const point = points[i];
    if (!point || point.length < 2) {
      continue;
    }

    const [x, y] = point;
    if (!Number.isFinite(x) || !Number.isFinite(y)) {
      continue;
    }

    aabb2AddPoint(bb, x, y);
  }

  return bb;
}

export function aabb2ByCenterWidthHeight(centerX: number, centerY: number, width: number, height: number): AABB2 {
  return aabb2New(centerX - width / 2, centerY - height / 2, centerX + width / 2, centerY + height / 2);
}

export function aabb2ByMinXYWidthHeight(x: number, y: number, width: number, height: number): AABB2 {
  return { minX: x, minY: y, maxX: x + width, maxY: y + height };
}

export function aabb2Scale(a: AABB2, s: number, originX: number = 0, originY: number = 0): AABB2 {
  a.minX = (a.minX - originX) * s + originX;
  a.maxX = (a.maxX - originX) * s + originX;
  a.minY = (a.minY - originY) * s + originY;
  a.maxY = (a.maxY - originY) * s + originY;
  return a;
}

export function aabb2ContainsAABB2(a: AABB2, b: AABB2): boolean {
  return b.minX >= a.minX && b.minY >= a.minY && b.maxX <= a.maxX && b.maxY <= a.maxY;
}

/**
 * Returns true if AABB `b` is strictly inside AABB `a` with at least `epsilon` gap on every
 * side. This rejects cases where `b` merely shares a boundary with `a` due to floating-point
 * noise (e.g. adjacent rooms whose edges differ by ~1e-12).
 */
export function aabb2StrictlyContainsAABB2(a: AABB2, b: AABB2, epsilon = DIST_EPSILON): boolean {
  return (
    a.minX < b.minX - epsilon && a.minY < b.minY - epsilon && a.maxX > b.maxX + epsilon && a.maxY > b.maxY + epsilon
  );
}

export function aabb2ContainsVec2(bb: AABB2, x: number, y: number) {
  return x >= bb.minX && y >= bb.minY && x <= bb.maxX && y <= bb.maxY;
}

export function aabb2Overlaps(a: AABB2, b: AABB2, epsilon = DIST_EPSILON): boolean {
  return (
    intervalsOverlap(a.minX, a.maxX, b.minX, b.maxX, epsilon) &&
    intervalsOverlap(a.minY, a.maxY, b.minY, b.maxY, epsilon)
  );
}

export function aabb2XOverlap(a: AABB2, b: AABB2): number {
  return max(0, min(a.maxX, b.maxX) - max(a.minX, b.minX));
}

export function aabb2YOverlap(a: AABB2, b: AABB2): number {
  return max(0, min(a.maxY, b.maxY) - max(a.minY, b.minY));
}

export function aabb2OverlapArea(a: AABB2, b: AABB2): number {
  const xOverlap = max(0, min(a.maxX, b.maxX) - max(a.minX, b.minX));
  const yOverlap = max(0, min(a.maxY, b.maxY) - max(a.minY, b.minY));
  return xOverlap * yOverlap;
}

export function aabb2Union(a: AABB2, b: AABB2): AABB2 {
  return aabb2New(min(a.minX, b.minX), min(a.minY, b.minY), max(a.maxX, b.maxX), max(a.maxY, b.maxY));
}

export function aabb2Intersect(a: AABB2, b: AABB2): AABB2 {
  return aabb2New(max(a.minX, b.minX), max(a.minY, b.minY), min(a.maxX, b.maxX), min(a.maxY, b.maxY));
}

export function aabb2Disjoint(a: AABB2, b: AABB2): boolean {
  return a.minX > b.maxX || a.minY > b.maxY || a.maxX < b.minX || a.maxY < b.minY;
}

export function aabb2UnionMutable(target: AABB2, source: AABB2): AABB2 {
  target.minX = min(target.minX, source.minX);
  target.minY = min(target.minY, source.minY);
  target.maxX = max(target.maxX, source.maxX);
  target.maxY = max(target.maxY, source.maxY);
  return target;
}

export function aabb2UnionAll(bbs: AABB2[]): AABB2 {
  return bbs.reduce(aabb2Union, {
    minX: Number.POSITIVE_INFINITY,
    maxX: Number.NEGATIVE_INFINITY,
    minY: Number.POSITIVE_INFINITY,
    maxY: Number.NEGATIVE_INFINITY,
  });
}

export function aabb2Equal(a: AABB2, b: AABB2, tol = 1e-3): boolean {
  if (!a || !b) {
    return false;
  }

  return vec2Dist(a.minX, a.minY, b.minX, b.minY) <= tol && vec2Dist(a.maxX, a.maxY, b.maxX, b.maxY) <= tol;
}

export function aabb2Center(a: AABB2) {
  const { minX, minY, maxX, maxY } = a;
  vec2Result[0] = (minX + maxX) / 2;
  vec2Result[1] = (minY + maxY) / 2;
  return vec2Result;
}

export function aabb2Area(a: AABB2) {
  const dx = a.maxX - a.minX;
  const dy = a.maxY - a.minY;
  return dx * dy;
}

export function aabb2DiagLength(a: AABB2): number {
  const dx = a.maxX - a.minX;
  const dy = a.maxY - a.minY;
  return sqrt(dx * dx + dy * dy);
}

export function aabb2Copy({ minX, minY, maxX, maxY }: AABB2): AABB2 {
  return aabb2New(minX, minY, maxX, maxY);
}

export function aabb2SetCenter(a: AABB2, cx: number, cy: number): AABB2 {
  const [oldX, oldY] = aabb2Center(a);

  return aabb2Move(a, cx - oldX, cy - oldY);
}

export function aabb2Move(a: AABB2, dx: number, dy: number): AABB2 {
  a.minX += dx;
  a.minY += dy;
  a.maxX += dx;
  a.maxY += dy;

  return a;
}

export function aabb2AddPoint(a: AABB2, x: number, y: number): AABB2 {
  a.minX = x < a.minX ? x : a.minX;
  a.minY = y < a.minY ? y : a.minY;
  a.maxX = x > a.maxX ? x : a.maxX;
  a.maxY = y > a.maxY ? y : a.maxY;
  return a;
}

export function aabb2AddAABB2(a: AABB2, b: AABB2): AABB2 {
  aabb2AddPoint(a, b.minX, b.minY);
  aabb2AddPoint(a, b.maxX, b.maxY);
  return a;
}

export function transformAABB2Basis(aabb: AABB2, srcPlane: Plane, dstPlane: Plane): AABB2 {
  const corners: [number, number][] = [
    [aabb.minX, aabb.minY],
    [aabb.minX, aabb.maxY],
    [aabb.maxX, aabb.maxY],
    [aabb.maxX, aabb.minY],
  ];

  const [x0, y0, z0] = planeEval(srcPlane, corners[0][0], corners[0][1]);
  const [u0, v0] = planeProject(dstPlane, x0, y0, z0);

  let minX = u0;
  let minY = v0;
  let maxX = u0;
  let maxY = v0;

  for (let i = 1; i < corners.length; i++) {
    const [x, y, z] = planeEval(srcPlane, corners[i][0], corners[i][1]);
    const [u, v] = planeProject(dstPlane, x, y, z);
    if (u < minX) {
      minX = u;
    }
    if (v < minY) {
      minY = v;
    }
    if (u > maxX) {
      maxX = u;
    }
    if (v > maxY) {
      maxY = v;
    }
  }

  aabb.minX = minX;
  aabb.minY = minY;
  aabb.maxX = maxX;
  aabb.maxY = maxY;

  return aabb;
}

export function aabb2Grow(a: AABB2, deltaX: number, deltaY: number): AABB2 {
  let minX = a.minX - deltaX;
  let maxX = a.maxX + deltaX;
  let minY = a.minY - deltaY;
  let maxY = a.maxY + deltaY;

  if (minX > maxX) {
    [minX, maxX] = [maxX, minX];
  }

  if (minY > maxY) {
    [minY, maxY] = [maxY, minY];
  }

  a.minX = minX;
  a.minY = minY;
  a.maxX = maxX;
  a.maxY = maxY;

  return a;
}

export function aabb2CenterWidthHeight(a: AABB2): [number, number, number, number] {
  return [(a.maxX + a.minX) / 2, (a.maxY + a.minY) / 2, a.maxX - a.minX, a.maxY - a.minY];
}

export function aabb2MinWidthHeight(a: AABB2): [number, number, number, number] {
  return [a.minX, a.minY, a.maxX - a.minX, a.maxY - a.minY];
}

/**
 * Calculates the axis-aligned bounding box (AABB) for a potentially rotated set of points.
 *
 * @param pls - An array of points.
 * @param rotationRad - The rotation angle in radians. Defaults to 0.
 * @param centerX - The x-coordinate of the center point. Defaults to 0.
 * @param centerY - The y-coordinate of the center point. Defaults to 0.
 * @returns The calculated AABB (minX, maxX, minY, maxY) or null if the input array is empty.
 */
export function aabb2ByArray(
  pls: Array<Float64Array | Float32Array | number[]>,
  rotationRad: number = 0,
  centerX: number = 0,
  centerY: number = 0,
): AABB2 | null {
  if (!Array.isArray(pls) || pls.length === 0) {
    return null;
  }
  const aabb = {
    minX: Number.POSITIVE_INFINITY,
    maxX: Number.NEGATIVE_INFINITY,
    minY: Number.POSITIVE_INFINITY,
    maxY: Number.NEGATIVE_INFINITY,
  };
  let hasFinitePoint = false;

  if (rotationRad !== 0) {
    const transform = mat3RotateAroundPoint(centerX, centerY, rotationRad);

    for (const line of pls) {
      if (!line || line.length < 2) {
        continue;
      }

      for (let i = 0, l = line.length - 1; i < l; i += 2) {
        const lineX = line[i];
        const lineY = line[i + 1];
        if (!Number.isFinite(lineX) || !Number.isFinite(lineY)) {
          continue;
        }

        hasFinitePoint = true;
        const [x, y] = mat3Dot(transform, line[i], line[i + 1]);
        aabb2AddPoint(aabb, x, y);
      }
    }
  } else {
    for (const line of pls) {
      if (!line || line.length < 2) {
        continue;
      }

      for (let i = 0, l = line.length - 1; i < l; i += 2) {
        const lineX = line[i];
        const lineY = line[i + 1];
        if (!Number.isFinite(lineX) || !Number.isFinite(lineY)) {
          continue;
        }

        hasFinitePoint = true;
        aabb2AddPoint(aabb, lineX, lineY);
      }
    }
  }

  return hasFinitePoint ? aabb : null;
}

/**
 * Calculates the axis-aligned bounding box (AABB) for an array of potentially rotated 2D polylines.
 *
 * @param pls - The array of 2D polylines.
 * @param rotationRad - The rotation angle in radians (default: 0).
 * @param centerX - The x-coordinate of the center point (default: 0).
 * @param centerY - The y-coordinate of the center point (default: 0).
 * @returns The axis-aligned bounding box (AABB) or null if the input is an empty array.
 */
export function aabb2ByPolyLine2s(
  pls: PolyLine2[],
  rotationRad: number = 0,
  centerX: number = 0,
  centerY: number = 0,
): AABB2 | null {
  return aabb2ByArray(pls, rotationRad, centerX, centerY);
}

/**
 * Computes a new bounding box based on the rotated corner points of the input bounding box.
 */
export function aabb2Rotate(a: AABB2, rotationRad: number = 0, centerX: number = 0, centerY: number = 0): AABB2 {
  const rotatedAABB = aabb2ByArray(
    [[a.minX, a.minY, a.maxX, a.minY, a.maxX, a.maxY, a.minX, a.maxY]],
    rotationRad,
    centerX,
    centerY,
  );

  return rotatedAABB === null ? a : rotatedAABB;
}

export function aabb2SignedDistanceTo(a: AABB2, x: number, y: number): number {
  const centerX = (a.minX + a.maxX) * 0.5;
  const centerY = (a.minY + a.maxY) * 0.5;
  const sizeX = a.maxX - a.minX;
  const sizeY = a.maxY - a.minY;
  const qX = Math.abs(x - centerX) - sizeX * 0.5;
  const qY = Math.abs(y - centerY) - sizeY * 0.5;
  const qXMax = Math.max(qX, 0.0);
  const qYMax = Math.max(qY, 0.0);
  return Math.sqrt(qXMax * qXMax + qYMax * qYMax) + Math.min(Math.max(qX, qY), 0.0);
}

export function aabb2IsValid(a: AABB2): boolean {
  return a.maxX >= a.minX && a.maxY >= a.minY;
}

export function aabb2XLength(a: AABB2): number {
  return a.maxX - a.minX;
}

export function aabb2YLength(a: AABB2): number {
  return a.maxY - a.minY;
}

export function aabb2Compare(a: AABB2, b: AABB2): boolean {
  return a.minX === b.minX && a.maxX === b.maxX && a.minY === b.minY && a.maxY === b.maxY;
}

/**
 * This function determines the relative position of a point (px,py) in relation to an axis-
 * aligned bounding box aabb in 2D space.
 */
export function aabb2PointGridPosition(aabb: AABB2, px: number, py: number): AABB2GridPosition {
  const { minX, minY, maxX, maxY } = aabb;

  // Top regions
  if (px < minX && py > maxY) {
    return AABB2GridPosition.TL;
  }
  if (px >= minX && px <= maxX && py > maxY) {
    return AABB2GridPosition.TC;
  }
  if (px > maxX && py > maxY) {
    return AABB2GridPosition.TR;
  }

  // Middle regions
  if (px < minX && py >= minY && py <= maxY) {
    return AABB2GridPosition.ML;
  }
  if (px >= minX && px <= maxX && py >= minY && py <= maxY) {
    return AABB2GridPosition.MC;
  }
  if (px > maxX && py >= minY && py <= maxY) {
    return AABB2GridPosition.MR;
  }

  // Bottom regions
  if (px < minX && py < minY) {
    return AABB2GridPosition.BL;
  }
  if (px >= minX && px <= maxX && py < minY) {
    return AABB2GridPosition.BC;
  }
  if (px > maxX && py < minY) {
    return AABB2GridPosition.BR;
  }

  // Default case (shouldn't be reached due to the exhaustive checks above)
  throw new Error("Unexpected case for point position");
}

export function aabb2AspectRatio(aabb: AABB2): number {
  const width = aabb.maxX - aabb.minX;
  const height = aabb.maxY - aabb.minY;

  // To avoid division by zero, handle cases where height is zero
  if (height === 0) {
    throw new Error("Height of AABB2 cannot be zero when calculating aspect ratio");
  }

  const aspectRatio = width / height;
  return aspectRatio;
}

export function newOrUnionAABB2(aabb2: AABB2 | undefined, bb: AABB2 | undefined | null): AABB2 | undefined {
  if (!bb || aabb2Area(bb) < DIST_EPSILON_SQUARED) {
    return aabb2;
  }

  if (!aabb2) {
    aabb2 = bb;
  } else {
    aabb2 = aabb2Union(aabb2, bb);
  }
  return aabb2;
}
